import React, { useEffect, useRef, useState } from 'react'
import {
  MoreVerticalIcon,
  ArchiveIcon,
  ArchiveRestoreIcon,
  BanIcon,
  ShieldCheckIcon,
  TagIcon,
  Trash2Icon,
  Undo2Icon,
} from 'lucide-react'
import type { ConversationAction, ConversationState } from '../api/conversationActions'

interface MenuItem {
  action: ConversationAction
  label: string
  icon: React.ElementType
  danger?: boolean
}

const ITEMS_BY_STATE: Record<ConversationState, MenuItem[]> = {
  active: [
    { action: 'archive', label: 'Archive', icon: ArchiveIcon },
    { action: 'spam', label: 'Mark as spam', icon: BanIcon, danger: true },
    { action: 'delete', label: 'Delete', icon: Trash2Icon, danger: true },
  ],
  archived: [
    { action: 'unarchive', label: 'Unarchive', icon: ArchiveRestoreIcon },
    { action: 'spam', label: 'Mark as spam', icon: BanIcon, danger: true },
    { action: 'delete', label: 'Delete', icon: Trash2Icon, danger: true },
  ],
  spam: [
    { action: 'not_spam', label: 'Not spam', icon: ShieldCheckIcon },
    { action: 'delete', label: 'Delete', icon: Trash2Icon, danger: true },
  ],
  deleted: [{ action: 'restore', label: 'Restore', icon: Undo2Icon }],
}

interface Props {
  state: ConversationState
  onAction: (action: ConversationAction) => void
  /** When provided, shows a "Label…" item that opens the label picker. */
  onLabel?: () => void
  /** Smaller trigger for list rows. */
  compact?: boolean
  className?: string
}

/**
 * Kebab menu with the conversation lifecycle actions (Archive / Mark as spam /
 * Delete and their inverses). Used on Inbox list rows and the conversation
 * header. Clicks never bubble to the row so opening the menu doesn't select
 * the conversation.
 */
export function ConversationActionsMenu({ state, onAction, onLabel, compact = false, className = '' }: Props) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  const items = ITEMS_BY_STATE[state] || ITEMS_BY_STATE.active

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Conversation actions"
        className={
          compact
            ? 'p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors'
            : 'flex items-center px-1.5 py-1.5 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50'
        }
      >
        <MoreVerticalIcon className={compact ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-44 bg-white rounded-xl shadow-xl border border-gray-200 py-1">
          {onLabel && (
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                onLabel()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <TagIcon className="w-4 h-4" />
              Label…
            </button>
          )}
          {items.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.action}
                type="button"
                onClick={() => {
                  setOpen(false)
                  onAction(item.action)
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
